import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import ClockCountdown from "../Icons/ClockCountdown";

interface SessionWarningModalProps {
    show: boolean;
    minutesRemaining: number;
    onContinueSession: () => void;
    onLogout: () => void;
    isRenewing?: boolean;
}

export const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
    show,
    minutesRemaining,
    onContinueSession,
    onLogout,
    isRenewing = false
}) => {
    const [countdown, setCountdown] = useState(minutesRemaining);

    useEffect(() => {
        setCountdown(minutesRemaining);
    }, [minutesRemaining]);

    useEffect(() => {
        if (!show) return;

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 0) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - (1 / 60); // Decrease by 1 second converted to minutes
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [show]);

    const formatTime = (minutes: number): string => {
        const mins = Math.floor(minutes);
        const secs = Math.floor((minutes - mins) * 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const getTimeColor = (): string => {
        if (countdown <= 1) return "text-danger";
        if (countdown <= 2) return "text-warning";
        return "text-dark";
    };

    return (
        <Modal
            show={show}
            backdrop="static"
            keyboard={false}
            centered
            className="session-warning-modal"
        >
            <Modal.Header className="border-0 p-0">
                <div className="d-flex flex-column align-items-center w-100 justify-content-center gap-3">
                    <ClockCountdown size={32} showBackground={true} />
                    <Modal.Title className="text-center">
                        Your Session Is Expiring Soon!!
                    </Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body className="text-center p-0">
                <div className="body-text">
                    You will be automatically logged out in <span className={`${getTimeColor()} fw-bold`}>{formatTime(countdown)}</span> minutes.
                    To continue working please stay logged in.
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 p-0 d-flex flex-column gap-3 w-100">
                <Button
                    variant="primary"
                    size="sm"
                    onClick={onContinueSession}
                    disabled={isRenewing}
                    className="w-100 fw-bold"
                    data-testid="continue-session-btn"
                >
                    {isRenewing ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                            />
                            Extending Session...
                        </>
                    ) : (
                        "Continue Session"
                    )}
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onLogout}
                    disabled={isRenewing}
                    className="w-100"
                    data-testid="logout-btn"
                >
                    Log Out
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SessionWarningModal;